import { Formik } from 'formik';
import { useState, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import { placeSchema } from '../../../../../constants';
import { DAV_APIS } from '../../../../../adapters';
import { Alert } from '../../../../commons';
import PlaceForm from '../PlaceForm';
import { useSearchQuery } from '../../../../../hooks';
import DashboardLoader from '../../../sections/Loader/DashboardLoader';
import { Button } from '../../../../ui/Button';

const normalizeBlocks = (raw: any): any[] => {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return normalizeBlocks(JSON.parse(raw)); } catch { return []; } }
  if (Array.isArray(raw)) return raw.flatMap((item: any) => typeof item === 'string' ? normalizeBlocks(item) : [item]);
  return [];
};

const EditPlacePage: FC = () => {
  const [loading, setLoading] = useState(false);
  const [place, setPlace] = useState<any>({});
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();

  const placeId = useSearchQuery('place') as string;

  useEffect(() => {
    const fetchPlace = async () => {
      if (!placeId) return;
      setIsFetching(true);
      try {
        const res = await DAV_APIS.place.get.getPlaceById(placeId);
        if (res.status === 200) {
          setPlace(res.data.place);
        }
      } catch (error) {
        Alert({ variant: 'error', message: 'Failed to load place data' });
      }
      setIsFetching(false);
    };
    fetchPlace();
    window.scrollTo(0, 0);
  }, [placeId]);

  const submitHandler = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        name: values.name,
        descriptionTitle: values.descriptionTitle,
        description: values.description,
        location: values.location,
        country: values.country,
        file: values.file,
        blocks: values.blocks,
      };
      const res = await DAV_APIS.place.editPlace(data, placeId);
      setLoading(false);
      if (res.status === 200) {
        Alert({ variant: 'success', message: 'Place updated successfully' });
        navigate('/dashboard/places');
      }
    } catch (error) {
      setLoading(false);
      Alert({ variant: 'error', message: 'Failed to update place, please try again' });
    }
  };

  return (
    <div className='w-full space-y-6 pt-4 pb-10'>
      <div className='flex items-center'>
        <button
          onClick={() => navigate('/dashboard/places')}
          type='button'
          className='flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors font-semibold'
        >
          <MdArrowBackIosNew className='w-4 h-4' />
          <span>Back to Places</span>
        </button>
      </div>

      <div className='bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm min-h-[400px]'>
        <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center tracking-tight'>
          Edit Place
        </h1>

        {isFetching ? (
          <div className='py-20 flex justify-center'>
            <DashboardLoader />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              name: place.name || '',
              descriptionTitle: place.descriptionTitle || '',
              description: place.description || '',
              location: place.location || '',
              country: place.country?.id || place.country || '',
              file: '',
              imageCover: place.image || '',
              blocks: normalizeBlocks(place.blocks),
              blockTitle: '',
              blockDescription: '',
            }}
            validationSchema={placeSchema}
            onSubmit={async (values, { resetForm }) => {
              await submitHandler(values);
              resetForm();
            }}
          >
            {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
              <form onSubmit={handleSubmit} className='w-full'>
                <PlaceForm values={values} setFieldValue={setFieldValue} />
                <div className='flex justify-center mt-10 border-t border-gray-100 dark:border-gray-700 pt-8'>
                  <Button
                    type='submit'
                    variant='primary'
                    loading={loading}
                    disabled={!dirty || !isValid}
                    className='w-full sm:w-64'
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default EditPlacePage;
